import { useMemo } from "react";
import { motion } from "framer-motion";

function mulberry32(seed) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const FONT = "Helvetica Neue, Helvetica, Arial, sans-serif";
const BASE = 372;
const COUNT = 40;
const TOP = 8;

export default function AscentVisual() {
  const peaks = useMemo(() => {
    const rand = mulberry32(41);
    const scores = Array.from({ length: COUNT }, () => Math.pow(rand(), 1.6)).sort((a, b) => a - b);
    return scores.map((s, i) => ({
      x: 40 + i * 14.4,
      h: 30 + s * 280,
      w: 30 + s * 22,
      top: i >= COUNT - TOP,
    }));
  }, []);

  const threshold = BASE - peaks[COUNT - TOP].h;

  return (
    <div
      className="relative border border-white/10 bg-navy-light/60 p-2 sm:p-4"
      role="img"
      aria-label="Mountain-range chart: forty accounts drawn as peaks ranked by opportunity score, rising left to right; the eight highest peaks are gold and marked as priority targets above a threshold line"
      data-testid="ascent-visual"
    >
      <svg viewBox="0 0 640 440" className="h-auto w-full">
        <defs>
          <linearGradient id="peakGold" x1="0" y1="1" x2="0.6" y2="0">
            <stop offset="0" stopColor="#38BDF8" stopOpacity="0.85" />
            <stop offset="1" stopColor="#D4AF37" />
          </linearGradient>
          <linearGradient id="peakGrey" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0" stopColor="#334155" stopOpacity="0.9" />
            <stop offset="1" stopColor="#64748B" stopOpacity="0.5" />
          </linearGradient>
        </defs>

        <line x1="24" y1={BASE} x2="616" y2={BASE} stroke="rgba(255,255,255,0.12)" />

        {peaks.map((p, i) => (
          <motion.path
            key={i}
            d={`M ${p.x - p.w / 2} ${BASE} L ${p.x} ${BASE - p.h} L ${p.x + p.w / 2} ${BASE} Z`}
            fill={p.top ? "url(#peakGold)" : "url(#peakGrey)"}
            opacity={p.top ? 0.92 : 0.55}
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 + i * 0.055, duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformOrigin: `${p.x}px ${BASE}px` }}
          />
        ))}

        <motion.line
          x1="24"
          y1={threshold}
          x2="616"
          y2={threshold}
          stroke="rgba(212,175,55,0.55)"
          strokeWidth="1"
          strokeDasharray="3 6"
          initial={{ pathLength: 0, opacity: 0 }}
          whileInView={{ pathLength: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 2.6, duration: 1.6 }}
        />
        <motion.text
          x="28"
          y={threshold - 8}
          fill="#E2B857"
          fontSize="8.5"
          fontFamily={FONT}
          letterSpacing="2"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.9 }}
          viewport={{ once: true }}
          transition={{ delay: 3.4, duration: 1 }}
        >
          PRIORITY THRESHOLD
        </motion.text>

        {peaks.filter((p) => p.top).map((p, i) => (
          <motion.circle
            key={i}
            cx={p.x}
            cy={BASE - p.h - 7}
            r="2"
            fill="#E2B857"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 3 + i * 0.14, duration: 0.7 }}
          />
        ))}

        <text x="24" y="404" fill="#94A3B8" fontSize="10" fontFamily={FONT} letterSpacing="2">
          ACCOUNTS RANKED BY OPPORTUNITY
        </text>
        <text x="616" y="404" fill="#E2B857" fontSize="10" textAnchor="end" fontFamily={FONT} letterSpacing="2">
          {`TOP ${TOP} · PRIORITY TARGETS`}
        </text>
        <text x="24" y="424" fill="#475569" fontSize="8.5" fontFamily={FONT} letterSpacing="1.5">
          SECTOR FIT · ACTIVITY · COMMERCIAL RELEVANCE
        </text>
      </svg>
    </div>
  );
}
